import React, { useMemo } from 'react';
import { TQuizzResult, useQuizzStore } from '../../stores';
import { Box, Grid2, Modal } from '@mui/material';
import { ModalWrapper } from '../Wrappers/ModalWrapper';
import { useTimer } from '../../hooks/useTimer';
import { TMaterialStyles } from '../../theme';
import { useTranslation } from 'react-i18next';
import { ButtonOutlined } from '../Buttons/ButtonOutlined';
import TypographyWithShadow from '../Typography/TypographyWithShadow';
import { Icon } from '../Icon';

interface IClaimRewardsQuizzModal {
  isOpen: boolean;
  onClose: () => void;
  result: TQuizzResult | null;
}

export const ClaimRewardsQuizzModal: React.FC<IClaimRewardsQuizzModal> = ({
  isOpen,
  onClose,
  result,
}) => {
  const { t } = useTranslation();
  const { availableAt } = useQuizzStore();

  const isEarnRewards = useMemo(
    () => Boolean(result && result.correct > 3),
    [result],
  );
  const isAllRight = useMemo(
    () => result?.incorrect === 0,
    [result?.incorrect],
  );

  const timer = useTimer(availableAt, isEarnRewards);

  const [modalTitle, modalSubtitle] = useMemo(() => {
    let title = t('quizz.congratulations');
    let subtitle = t('quizz.rewards_claimed');

    if (!isEarnRewards) {
      title = t('quizz.what_a_pitty');
      subtitle = t('quizz.did_not_earn_rewards');
    }

    return [title, subtitle];
  }, [isEarnRewards, t]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalWrapper
        contentWrappperSx={materialStyles.contentWrapper}
        onClose={onClose}
      >
        <TypographyWithShadow
          shadowType="DARK"
          sx={
            !isEarnRewards
              ? materialStyles.modalTitle
              : materialStyles.smallModalTitle
          }
        >
          {modalTitle}
        </TypographyWithShadow>
        <TypographyWithShadow
          shadowType="DARK"
          sx={materialStyles.modalSubtitle}
        >
          {t('quizz.you_have_answered_properly_to')}
        </TypographyWithShadow>
        {result ? (
          <TypographyWithShadow
            shadowType="DARK"
            sx={materialStyles.questionsModalSubtitle}
          >
            {t('quizz.reverted_questions_with_size', {
              page: result.correct,
              size: result.correct + result.incorrect,
            })}
          </TypographyWithShadow>
        ) : null}
        <TypographyWithShadow
          sx={materialStyles.modalSubtitleLevelTwo}
          shadowType="DARK"
        >
          {modalSubtitle}
        </TypographyWithShadow>
        {!isEarnRewards ? (
          <TypographyWithShadow
            sx={materialStyles.modalTimer}
            shadowType="DARK"
          >
            {timer}
          </TypographyWithShadow>
        ) : (
          <Grid2 sx={materialStyles.rewardsContainer}>
            <Box sx={materialStyles.rewardContainer}>
              <Icon sx={materialStyles.rewardIcon} iconName="HippoCointsIcon" />
              <TypographyWithShadow
                sx={materialStyles.rewardsText}
                shadowType="DARK"
              >
                {t('common.points_with_count', { points: '7000' })}
              </TypographyWithShadow>
            </Box>
            {isAllRight ? (
              <Box sx={materialStyles.rewardContainer}>
                <Icon sx={materialStyles.rewardIcon} iconName="HintsIcon" />
                <TypographyWithShadow
                  sx={materialStyles.rewardsText}
                  shadowType="DARK"
                >
                  {t('common.hints_with_count', { hints: '3' })}
                </TypographyWithShadow>
              </Box>
            ) : null}
          </Grid2>
        )}
        <Box sx={materialStyles.buttonContainer}>
          <ButtonOutlined shadowType="DARK" onClick={onClose}>
            {t('common.main_menu')}
          </ButtonOutlined>
        </Box>
      </ModalWrapper>
    </Modal>
  );
};

const materialStyles: TMaterialStyles = {
  contentWrapper: {
    paddingTop: '50px',
    px: '29px',
    paddingBottom: '22px',
  },
  modalTitle: {
    fontFamily: 'Rubber Duck !important',
    textTransform: 'uppercase',
    textAlign: 'center',
    fontSize: '32px',
    lineHeight: '100%',
  },
  smallModalTitle: {
    fontFamily: 'Rubber Duck !important',
    textTransform: 'uppercase',
    textAlign: 'center',
    fontSize: '24px',
    lineHeight: '100%',
  },
  modalSubtitle: {
    fontSize: '16px',
    lineHeight: '100%',
    textAlign: 'center',
    fontWeight: '600',
    mt: '20px',
  },
  questionsModalSubtitle: {
    fontSize: '16px',
    lineHeight: '100%',
    textAlign: 'center',
    fontWeight: '700',
    mt: '4px',
  },
  modalSubtitleLevelTwo: {
    fontSize: '16px',
    lineHeight: '100%',
    textAlign: 'center',
    fontWeight: '600',
    mt: '18px',
  },
  modalTimer: {
    fontSize: '16px',
    lineHeight: '100%',
    textAlign: 'center',
    fontWeight: '600',
    mt: '18px',
  },
  rewardsText: {
    fontSize: '16px',
    lineHeight: '100%',
    textAlign: 'center',
    fontWeight: '600',
  },
  rewardsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '12px',
  },
  rewardContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '7px',
  },
  rewardIcon: {
    width: '29px',
    height: '29px',
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mt: '23px',
  },
};
